import {
  dsoCheckoutEndPoint,
  dsoOrdersEndpoint,
} from '../../../restUtility/restEndpoints';
import RestUtility from '../../../restUtility';
import { notifyError } from 'config/setupBugsnag';
import {
  DsoAddressClient,
  BillingAddressResponse,
  DsoBillingAddressGroupClient,
  SaveOrderAddressPair,
  ShippingAddressResponse,
  ShippingMethodResponse,
  ShippingMethodClient,
  DsoPaymentMethodResponse,
  DsoPaymentMethodClient,
  DsoPlaceOrderResponse,
  DsoPlaceOrderClient,
} from '../../types/dso/DsoCheckout.types';
import {
  DsoCart,
  DsoCartResponse,
  DsoCheckoutSummary,
  DsoCheckoutSummaryByProduct,
  DsoCheckoutSummaryProduct,
  DsoCheckoutSummaryShipmentByProduct,
} from 'types';
import { dsoCartService } from 'services';

export class DsoCheckoutService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async proceedToDsoCheckout(): Promise<DsoCart> {
    try {
      const response = await this.restUtility.post<DsoCartResponse>(
        `${dsoCheckoutEndPoint}/validate`,
        {}
      );
      return dsoCartService.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::proceedToDsoCheckout', error);
      throw error;
    }
  }

  getCartSummary(
    cart: DsoCart,
    shippingAddresses: DsoAddressClient[] | undefined
  ): DsoCheckoutSummary {
    return this.buildCartSummary(cart, shippingAddresses);
  }

  getCartSummaryByProducts(
    cart: DsoCart,
    shippingAddresses: DsoAddressClient[] | undefined
  ): DsoCheckoutSummaryByProduct {
    return this.buildCartSummaryByProducts(cart, shippingAddresses);
  }

  async getShippingAddresses(): Promise<DsoAddressClient[]> {
    try {
      const response = await this.restUtility.get<ShippingAddressResponse>(
        `${dsoCheckoutEndPoint}/shipping_addresses?per_page=10000&page=1`
      );
      return this.buildShippingAddress(response);
    } catch (error) {
      notifyError('ApiError:: DSO::getShippingAddresses', error);
      throw error;
    }
  }

  async getBillingAddresses(): Promise<DsoBillingAddressGroupClient[]> {
    try {
      const response = await this.restUtility.get<BillingAddressResponse>(
        `${dsoCheckoutEndPoint}/billing_addresses`
      );
      return this.buildBillingAddress(response);
    } catch (error) {
      notifyError('ApiError:: DSO::getBillingAddresses', error);
      throw error;
    }
  }

  async getBillingAddressesByShipAccount(
    shipAccountNumber: string
  ): Promise<DsoBillingAddressGroupClient[]> {
    try {
      const response = await this.restUtility.get<BillingAddressResponse>(
        `${dsoCheckoutEndPoint}/billing_addresses?shipping_address_account_number=${shipAccountNumber}`
      );
      return this.buildBillingAddress(response);
    } catch (error) {
      notifyError('ApiError:: DSO::getBillingAddressesByShipAccount', error);
      throw error;
    }
  }

  async saveAddress(addressPairs: SaveOrderAddressPair[]): Promise<DsoCart> {
    try {
      const response = await this.restUtility.put<DsoCartResponse>(
        `${dsoCheckoutEndPoint}/address`,
        {
          addresses: addressPairs.map(pair => ({
            ship_account_number: pair.shipAccountNumber,
            bill_account_number: pair.billAccountNumber,
          })),
        }
      );
      return dsoCartService.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::saveAddress', error);
      throw error;
    }
  }

  async getShippingMethods(): Promise<ShippingMethodClient[]> {
    try {
      const response = await this.restUtility.get<ShippingMethodResponse>(
        `${dsoCheckoutEndPoint}/shipping-methods`
      );
      return response.data.map(item => {
        return {
          id: item.id,
          name: item.name,
          description: item.description,
          price: item.price,
        };
      });
    } catch (error) {
      notifyError('ApiError:: DSO::getShippingMethods', error);
      throw error;
    }
  }

  async saveOrderShippingMethod(shippingMethodId: string): Promise<DsoCart> {
    try {
      const response = await this.restUtility.put<DsoCartResponse>(
        `${dsoCheckoutEndPoint}/shipping-methods`,
        {
          shipment_method_id: shippingMethodId,
        }
      );
      return dsoCartService.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::saveOrderShippingMethod', error);
      throw new Error('Failed to save order shipping method');
    }
  }

  async getPaymentMethods(): Promise<DsoPaymentMethodClient[]> {
    try {
      const response = await this.restUtility.get<DsoPaymentMethodResponse>(
        `${dsoCheckoutEndPoint}/payment-methods`
      );
      return response.data.map(item => {
        return {
          id: item.id,
          name: item.name,
          description: item.description,
          price: item.price,
        };
      });
    } catch (error) {
      notifyError('ApiError:: DSO::getPaymentMethods', error);
      throw error;
    }
  }

  async saveOrderPaymentMethod(paymentMethodId: string): Promise<DsoCart> {
    try {
      const response = await this.restUtility.put<DsoCartResponse>(
        `${dsoCheckoutEndPoint}/payment-methods`,
        {
          payment_method_id: paymentMethodId,
        }
      );
      return dsoCartService.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::saveOrderPaymentMethod', error);
      throw new Error('Failed to save order payment method');
    }
  }

  async placeOrder(): Promise<DsoPlaceOrderClient> {
    try {
      const response = await this.restUtility.post<DsoPlaceOrderResponse>(
        `${dsoOrdersEndpoint}`,
        {}
      );
      return {
        orderId: response.data.order_id,
        orderNumber: response.data.order_number,
      };
    } catch (error) {
      notifyError('ApiError:: DSO::placeOrder', error);
      throw error;
    }
  }

  buildShippingAddress(response: ShippingAddressResponse): DsoAddressClient[] {
    if (!response || !response.data || !Array.isArray(response.data)) {
      return [];
    }

    return response.data.map(item => {
      // This addressString will be displayed in the dropdown, will also be stored in the DB.
      const addressString = item.address;

      return {
        accountNumber: item.account_number,
        accountName: item.account_name,
        accountAddress: addressString,
      };
    });
  }

  buildBillingAddress(
    response: BillingAddressResponse
  ): DsoBillingAddressGroupClient[] {
    if (
      !response?.data?.shipping_addresses ||
      !response?.data?.billing_addresses
    ) {
      return [];
    }

    const { shipping_addresses, billing_addresses } = response.data;

    // Build lookup map: account_number → DsoAddressClient
    const billingMap = new Map<string, DsoAddressClient>();
    for (const bill of billing_addresses) {
      billingMap.set(bill.account_number, {
        accountNumber: bill.account_number,
        accountName: bill.account_name,
        accountAddress: bill.address,
      });
    }

    // For each shipping address, resolve its bill_to_account_numbers to full objects
    return shipping_addresses.map(ship => ({
      shipAccountNumber: ship.account_number,
      billingAddresses: (ship.bill_to_account_numbers ?? [])
        .map(num => billingMap.get(num))
        .filter((addr): addr is DsoAddressClient => !!addr),
    }));
  }

  buildCartSummary(
    cart: DsoCart,
    shippingAddresses: DsoAddressClient[] | undefined
  ): DsoCheckoutSummary {
    if (!shippingAddresses) {
      return {
        shipments: [],
        displayOrderSubTotal: cart.displayOrderSubTotal,
        displayOrderAdjustmentTotal: cart.displayOrderAdjustmentTotal,
        displayOrderNetTotal: cart.displayOrderNetTotal,
        displayOrderShippingTotal: cart.displayOrderShippingTotal,
        displayOrderTaxTotal: cart.displayOrderTaxTotal,
        displayOrderGrandTotal: cart.displayOrderGrandTotal,
        displayOrderDiscountTotal: cart.displayOrderDiscountTotal,
        displayOrderSavingsTotal: cart.displayOrderSavingsTotal,
      };
    }
    const shipmentMap = new Map<
      string,
      {
        accountName: string;
        shipAddress: string;
        products: DsoCheckoutSummaryProduct[];
      }
    >();

    for (const lineItem of cart.lineItems) {
      for (const child of lineItem.children) {
        const key = child.shipAccountNumber;
        if (!shipmentMap.has(key)) {
          const matchedAddress = shippingAddresses.find(
            address => address.accountNumber === key
          );
          shipmentMap.set(key, {
            accountName: matchedAddress?.accountName ?? '',
            shipAddress: matchedAddress?.accountAddress ?? '',
            products: [],
          });
        }

        const shipment = shipmentMap.get(key);
        const existing = shipment?.products.find(
          p => p.spreeVariantId === lineItem.spreeVariantId
        );

        if (existing) {
          existing.quantity += child.quantity;
        } else {
          shipment?.products.push({
            name: lineItem.name,
            imageUrl: lineItem.imageUrl ?? '/images/noimage.jpeg',
            slug: lineItem.slug,
            spreeVariantId: lineItem.spreeVariantId,
            displayUnitPrice: lineItem.displayUnitPrice,
            displayItemSubTotal: child.displaySubTotal,
            quantity: child.quantity,
          });
        }
      }
    }

    return {
      shipments: Array.from(shipmentMap.values()),
      displayOrderSubTotal: cart.displayOrderSubTotal,
      displayOrderAdjustmentTotal: cart.displayOrderAdjustmentTotal,
      displayOrderNetTotal: cart.displayOrderNetTotal,
      displayOrderShippingTotal: cart.displayOrderShippingTotal,
      displayOrderTaxTotal: cart.displayOrderTaxTotal,
      displayOrderGrandTotal: cart.displayOrderGrandTotal,
      displayOrderDiscountTotal: cart.displayOrderDiscountTotal,
      displayOrderSavingsTotal: cart.displayOrderSavingsTotal,
    };
  }

  buildCartSummaryByProducts(
    cart: DsoCart,
    shippingAddresses: DsoAddressClient[] | undefined
  ): DsoCheckoutSummaryByProduct {
    if (!shippingAddresses) {
      return {
        products: [],
        displayOrderSubTotal: cart.displayOrderSubTotal,
        displayOrderAdjustmentTotal: cart.displayOrderAdjustmentTotal,
        displayOrderNetTotal: cart.displayOrderNetTotal,
        displayOrderShippingTotal: cart.displayOrderShippingTotal,
        displayOrderTaxTotal: cart.displayOrderTaxTotal,
        displayOrderGrandTotal: cart.displayOrderGrandTotal,
      };
    }

    const productMap = new Map<number, DsoCheckoutSummaryShipmentByProduct>();

    for (const lineItem of cart.lineItems) {
      if (!productMap.has(lineItem.spreeVariantId)) {
        productMap.set(lineItem.spreeVariantId, {
          name: lineItem.name,
          imageUrl: lineItem.imageUrl ?? '/images/noimage.jpeg',
          slug: lineItem.slug,
          spreeVariantId: lineItem.spreeVariantId,
          displayItemSubTotal: lineItem.displaySubTotal,
          shipAddresses: [],
        });
      }

      const product = productMap.get(lineItem.spreeVariantId);

      for (const child of lineItem.children) {
        const shipAddress =
          shippingAddresses.find(
            address => address.accountNumber === child.shipAccountNumber
          )?.accountAddress ?? '';

        product?.shipAddresses.push({
          shipAddress: shipAddress,
          quantity: child.quantity,
        });
      }
    }

    return {
      products: Array.from(productMap.values()),
      displayOrderSubTotal: cart.displayOrderSubTotal,
      displayOrderAdjustmentTotal: cart.displayOrderAdjustmentTotal,
      displayOrderNetTotal: cart.displayOrderNetTotal,
      displayOrderShippingTotal: cart.displayOrderShippingTotal,
      displayOrderTaxTotal: cart.displayOrderTaxTotal,
      displayOrderGrandTotal: cart.displayOrderGrandTotal,
    };
  }
}
